/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : FilterByOptionsTickets.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import CustomButton from "../buttons/CustomButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SortByOptionsTickets from "./SortByOptionsTickets";
import CloseIcon from "@mui/icons-material/Close";

const buildLegalRequirementsDetails = (legalRequirementsDetails, selectedChapter) => {
  if (!Array.isArray(legalRequirementsDetails) || legalRequirementsDetails.length === 0) {
    return [];
  }

  return legalRequirementsDetails
    .filter((data) => selectedChapter?.includes(data?.chapter))
    .reduce((acc, data) => {
      if (!acc[data?.chapter]) {
        acc[data?.chapter] = [];
      }

      acc[data?.chapter].push({
        lr_id: data?.lr_id,
        req_number: data?.req_number,
      });

      return acc;
    }, {});
};

const buildFilterSections = ({
  tabValue,
  searchTicketId,
  searchIntrId,
  ticketsType,
  appMetaDetails,
  UserDetails,
  selectedProjectDetails,
  selectedVariantDetails,
  EEDetails,
  LSDetails,
  selectedStandard,
  selectedChapter,
}) => {
  const sections = [];

  if (tabValue === 2) {
    sections.push({ title: "Ticket ID", value: searchTicketId });
  }
  if (tabValue === 1) {
    sections.push({ title: "Intersection ID", value: searchIntrId });
  }

  sections.push({
    title: "Ticket Type",
    ticket_type: {
      options: [
        { value: ticketsType?.EE, label: "EE Component" },
        { value: ticketsType?.SWM, label: "Software Modules" },
        { value: ticketsType?.VEHICLE, label: "Vehicle Component" },
      ],
    },
  });

  sections.push({
    title: "Ticket Status",
    status_list:
      appMetaDetails?.snow_ticket_status?.length > 0
        ? appMetaDetails.snow_ticket_status
        : [],
  });

  const oemDetails =
    UserDetails?.usersList?.length > 0 &&
      selectedProjectDetails?.co_owners?.length > 0
      ? UserDetails?.usersList?.filter((oem) =>
        selectedProjectDetails?.co_owners
          ?.concat(selectedProjectDetails?.owner_details[0]?.user_id)
          ?.includes(oem?.user_id),
      )
      : [];

  sections.push({
    title: "TA requestors",
    oem_details: oemDetails,
  });

  sections.push({
    title: "TA reviewers",
    tech_details:
      UserDetails?.usersList?.length > 0
        ? UserDetails?.usersList?.filter(
          (user) => user?.role_details?.role_type === 1,
        )
        : [],
  });

  sections.push({
    title: "Zones",
    zone_details:
      selectedVariantDetails?.zone_details?.length > 0
        ? selectedVariantDetails.zone_details
        : [],
  });

  sections.push({
    title: "EE Components",
    ee_components_details:
      EEDetails?.eeComponentsList?.length > 0 ? EEDetails.eeComponentsList : [],
  });

  sections.push({
    title: "Software Modules",
    ee_details: {
      title: "E/E Components ",
      ee_components_details:
        EEDetails?.eeComponentsList?.length > 0
          ? EEDetails.eeComponentsList
          : [],
    },
    swm_details: {
      title: "SWM Components",
      software_modules_details:
        EEDetails?.swModulesList?.length > 0 ? EEDetails?.swModulesList : [],
    },
  });

  sections.push({
    title: "Vehicle Components",
    vehicle_components_details:
      selectedVariantDetails?.vehicle_components?.length > 0
        ? selectedVariantDetails.vehicle_components
        : [],
  });

  sections.push({
    title: "Legal Details",
    legal_standard: {
      title: "Standards",
      legal_standard_details:
        LSDetails?.legalStandardDetails?.length > 0
          ? LSDetails.legalStandardDetails
          : [],
    },
    legal_chapters: {
      title: "Chapters",
      legal_chapters_details:
        LSDetails?.legalStandardDetails?.length > 0
          ? LSDetails?.legalStandardDetails?.filter((data) =>
            selectedStandard?.some((id) => data?.ls_id.includes(id)),
          )
          : [],
    },
    legal_requirements: {
      title: "Legal Requirements",
      legal_requirements_details: buildLegalRequirementsDetails(
        LSDetails?.legalRequirementsDetails,
        selectedChapter,
      ),
    },
  });

  return sections;
};


const SectionTitle = ({ title }) => (
  <Typography sx={{ fontWeight: "bold" }}>{title}</Typography>
);

const TicketIdSection = ({
  data,
  reqObj,
  fetchTickets,
  setSearchTicketId,
  setSearchIntrId,
}) => (
  <Accordion expanded={true}>
    <AccordionDetails>
      <Box sx={{ padding: "7px" }}>
        <MemoizedTextField
          reqObj={reqObj}
          fetchTickets={fetchTickets}
          setSearchTicketId={setSearchTicketId}
          setSearchIntrId={setSearchIntrId}
          data={data}
        />
      </Box>
    </AccordionDetails>
  </Accordion>
);

const TicketTypeSection = ({
  data,
  selectedTicketsType,
  setSelectedTicketsType,
}) => (
  <Accordion expanded={true}>
    <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails>
      <FormControl
        sx={{
          size: "small",
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        <RadioGroup
          value={selectedTicketsType}
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
          onChange={(e) => setSelectedTicketsType(e.target.value)}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {data?.ticket_type?.options?.map((option, idx) => (
            <FormControlLabel
              key={idx}
              value={option?.value}
              control={<Radio sx={{ fontSize: 16 }} />}
              label={
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {option?.label}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>
    </AccordionDetails>
  </Accordion>
);

const TicketStatusSection = ({
  data,
  handleCheckboxChange,
  selectTicketStatus,
}) => (
  <Accordion defaultExpanded={true}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        minHeight: "200px",
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
        padding: 2,
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        {Object?.values(data?.status_list)?.map((item, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                onChange={(e) =>
                  handleCheckboxChange(e, item?.value, data?.title)
                }
                checked={selectTicketStatus.includes(item?.value)}
              />
            }
            label={
              <Typography sx={{ fontSize: { md: "0.75rem" } }}>
                {item?.key}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const RequestorsSection = ({ data, handleCheckboxChange, selectOEM }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        {data?.oem_details?.map((item, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                onChange={(e) =>
                  handleCheckboxChange(e, item?.user_id, data?.title)
                }
                checked={selectOEM.includes(item?.user_id)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem" }}>
                {item?.fullname}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const ReviewersSection = ({ data, handleCheckboxChange, selectTECH }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          justifyContent: "flex-start",
        }}
      >
        {data?.tech_details?.map((item, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                onChange={(e) =>
                  handleCheckboxChange(e, item?.user_id, data?.title)
                }
                checked={selectTECH.includes(item?.user_id)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem" }}>
                {item?.fullname}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const ZonesSection = ({ data, handleCheckboxChange, selectedZones }) => (
  <Accordion>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {data?.zone_details?.map((option, index) => (
          <FormControlLabel
            key={index}
            control={
              <Checkbox
                onChange={(e) =>
                  handleCheckboxChange(e, option?.zone?.position, data?.title)
                }
                checked={selectedZones.includes(option?.zone?.position)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem" }}>
                {option?.zone?.zone_name}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const EeComponentsSection = ({
  data,
  handleCheckboxChange,
  selectedEE,
  selectedTicketsType,
  ticketsType,
}) => (
  <Accordion
    disabled={
      parseInt(selectedTicketsType) === ticketsType?.VEHICLE ||
        parseInt(selectedTicketsType) === ticketsType?.SWM
        ? true
        : false
    }
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {Object?.values(data?.ee_components_details)?.map((option) => (
          <FormControlLabel
            key={option?.ee_id}
            control={
              <Checkbox
                onChange={(e) =>
                  handleCheckboxChange(e, option?.ee_id, data?.title)
                }
                checked={selectedEE.includes(option?.ee_id)}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.875rem" }}>
                {option?.supplier_name}
              </Typography>
            }
          />
        ))}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const VehicleComponentsSection = ({
  data,
  handleCheckboxChange,
  selectedVC,
  selectedTicketsType,
  ticketsType,
}) => (
  <Accordion
    disabled={
      parseInt(selectedTicketsType) === ticketsType?.EE ||
        parseInt(selectedTicketsType) === ticketsType?.SWM
        ? true
        : false
    }
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails
      sx={{
        maxHeight: "300px",
        overflowY: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "#888 #f1f1f1",
      }}
    >
      <FormGroup
        sx={{
          size: "small",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {Object?.values(data?.vehicle_components_details)?.map(
          (componentData, index) => (
            <FormControlLabel
              key={index}
              control={
                <Checkbox
                  onChange={(e) =>
                    handleCheckboxChange(
                      e,
                      componentData?.id,
                      data?.title,
                    )
                  }
                  checked={selectedVC.includes(componentData?.id)}
                />
              }
              label={
                <Typography sx={{ fontSize: "0.875rem" }}>
                  {componentData?.component}
                </Typography>
              }
            />
          ),
        )}
      </FormGroup>
    </AccordionDetails>
  </Accordion>
);

const LegalDetailsSection = ({
  data,
  handleCheckboxChange,
  selectedStandard,
  selectedChapter,
  selectedLR,
}) => (
  <Accordion>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls="panel1a-content"
      id="panel1a-header"
    >
      <SectionTitle title={data?.title} />
    </AccordionSummary>

    <Grid container spacing={2}>
      <Grid item md={12}>
        <FormLabel component="legend">
          {data?.legal_standard?.title}
        </FormLabel>
        <AccordionDetails
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#888 #f1f1f1",
          }}
        >
          <FormGroup
            sx={{
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            {data?.legal_standard?.legal_standard_details?.length > 0 ? (
              data?.legal_standard?.legal_standard_details.map((item, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      onChange={(e) =>
                        handleCheckboxChange(
                          e,
                          item?.ls_id,
                          data?.legal_standard?.title,
                        )
                      }
                      checked={selectedStandard.includes(item?.ls_id)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "0.875rem" }}>
                      {item?.standard_number}
                    </Typography>
                  }
                />
              ))
            ) : (
              <Typography sx={{ fontSize: "0.875rem", color: "gray" }}>
                Nothing selected
              </Typography>
            )}
          </FormGroup>
        </AccordionDetails>
      </Grid>
      <Grid item md={12}>
        <FormLabel component="legend">{data?.legal_chapters?.title}</FormLabel>
        <AccordionDetails
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#888 #f1f1f1",
          }}
        >
          <FormGroup
            sx={{
              mt: "10px",
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            {data?.legal_chapters?.legal_chapters_details?.length > 0 ? (
              data?.legal_chapters?.legal_chapters_details.map((item, index) => (
                <FormGroup
                  key={index}
                  sx={{
                    mt: "10px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <FormLabel component="legend">
                    {item?.standard_number}
                  </FormLabel>
                  {item?.chapters?.length > 0 ? (
                    item?.chapters.map((option, idx) => (
                      <FormControlLabel
                        key={idx}
                        sx={{ mt: "10px", mb: "10px" }}
                        control={
                          <Checkbox
                            onChange={(e) =>
                              handleCheckboxChange(
                                e,
                                option,
                                data?.legal_chapters?.title,
                              )
                            }
                            checked={selectedChapter.includes(option)}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              textAlign: "start",
                            }}
                          >
                            {option}
                          </Typography>
                        }
                      />
                    ))
                  ) : (
                    <Typography sx={{ fontSize: "0.875rem", color: "gray" }}>
                      No chapters available
                    </Typography>
                  )}
                </FormGroup>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.875rem", color: "gray" }}>
                No Legal Standard Selected
              </Typography>
            )}
          </FormGroup>
        </AccordionDetails>
      </Grid>
      <Grid item md={12}>
        <FormLabel component="legend">
          {data?.legal_requirements?.title}
        </FormLabel>
        <AccordionDetails
          sx={{
            maxHeight: "300px",
            overflowY: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#888 #f1f1f1",
          }}
        >
          <FormGroup
            sx={{
              size: "small",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {data?.legal_requirements?.legal_requirements_details &&
              Object.entries(data?.legal_requirements?.legal_requirements_details)
                .length > 0 ? (
              Object.entries(
                data?.legal_requirements?.legal_requirements_details,
              ).map(([key, item], index) => (
                <FormGroup key={index}>
                  <FormLabel component="legend">{key}</FormLabel>
                  {item?.length > 0 ? (
                    item.map((option, idx) => (
                      <FormControlLabel
                        key={idx}
                        control={
                          <Checkbox
                            onChange={(e) =>
                              handleCheckboxChange(
                                e,
                                option?.lr_id,
                                data?.legal_requirements?.title,
                              )
                            }
                            checked={selectedLR.includes(option?.lr_id)}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "0.875rem" }}>
                            {option?.req_number}
                          </Typography>
                        }
                      />
                    ))
                  ) : (
                    <Typography sx={{ fontSize: "0.875rem", color: "gray" }}>
                      No requirements available for {key}
                    </Typography>
                  )}
                </FormGroup>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.875rem", color: "gray" }}>
                No Legal Chapters Selected
              </Typography>
            )}
          </FormGroup>
        </AccordionDetails>
      </Grid>
    </Grid>
  </Accordion>
);

const SoftwareModulesSection = ({
  data,
  handleCheckboxChange,
  selectSWMEE,
  selectedSWM,
  selectedTicketsType,
  ticketsType,
}) => (
  <Accordion
    disabled={
      parseInt(selectedTicketsType) === ticketsType?.EE ||
        parseInt(selectedTicketsType) === ticketsType?.VEHICLE
        ? true
        : false
    }
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls="panel1a-content"
      id="panel1a-header"
    >
      <SectionTitle title={data?.title} />
    </AccordionSummary>
    <AccordionDetails>
      <Grid container spacing={2}>
        <Grid item md={6}>
          <FormGroup
            sx={{
              size: "small",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              maxHeight: "300px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            <FormLabel component="legend">{data?.ee_details?.title}</FormLabel>
            {data?.ee_details?.ee_components_details?.map((item, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={selectSWMEE === item?.ee_id}
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        item?.ee_id,
                        data?.ee_details?.title,
                      )
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {item?.supplier_name}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Grid>

        <Grid item md={6}>
          <FormGroup
            sx={{
              size: "small",
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              maxHeight: "300px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            <FormLabel component="legend">{data?.swm_details?.title}</FormLabel>
            {data?.swm_details?.software_modules_details?.map((item, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    onChange={(e) =>
                      handleCheckboxChange(
                        e,
                        item?.swm_id,
                        data?.swm_details?.title,
                      )
                    }
                    checked={selectedSWM.includes(item?.swm_id)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "0.875rem" }}>
                    {item?.description}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
        </Grid>
      </Grid>
    </AccordionDetails>
  </Accordion>
);

const FilterSection = (props) => {
  const { data } = props;
  switch (data?.title) {
    case "Ticket ID":
    case "Intersection ID":
      return <TicketIdSection {...props} />;
    case "Ticket Type":
      return <TicketTypeSection {...props} />;
    case "Ticket Status":
      return <TicketStatusSection {...props} />;
    case "TA requestors":
      return <RequestorsSection {...props} />;
    case "TA reviewers":
      return <ReviewersSection {...props} />;
    case "Zones":
      return <ZonesSection {...props} />;
    case "EE Components":
      return <EeComponentsSection {...props} />;
    case "Vehicle Components":
      return <VehicleComponentsSection {...props} />;
    case "Legal Details":
      return <LegalDetailsSection {...props} />;
    case "Software Modules":
      return <SoftwareModulesSection {...props} />;
    default:
      return null;
  }
};

function FilterByOptionsTickets({
  UserDetails,
  selectOEM,
  selectTECH,
  ticketsType,
  appMetaDetails,
  selectedVariantDetails,
  EEDetails,
  LSDetails,
  selectedTicketsType,
  setSelectedTicketsType,
  selectTicketStatus,
  handleCheckboxChange,
  selectedZones,
  selectedEE,
  selectedVC,
  selectedStandard,
  selectedChapter,
  selectedLR,
  selectSWMEE,
  selectedSWM,
  setSearchTicketId,
  searchTicketId,
  setSearchIntrId,
  searchIntrId,
  handleClearAllFilter,
  fetchTickets,
  reqObj,
  tabValue,
  selectedProjectDetails,
  selectColumn,
  selectOrder,
  setSelectColumn,
  setSelectOrder,
  setFilterDrawerOpen,
}) {


  const filterSections = buildFilterSections({
    tabValue,
    searchTicketId,
    searchIntrId,
    ticketsType,
    appMetaDetails,
    UserDetails,
    selectedProjectDetails,
    selectedVariantDetails,
    EEDetails,
    LSDetails,
    selectedStandard,
    selectedChapter,
  });

  return (
    <div>
      <Box
        sx={{
          boxShadow: 4,
          padding: "5px",
          ml: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              position: "sticky",
              mb: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 0.5, sm: 0.75, md: 1 },
                fontWeight: 500,
                fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1rem" },
              }}
            >
              Filter Tickets
            </Typography>
            <IconButton
              size="small"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{ fontSize: { xs: 15, sm: 17 } }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
          <Box>
            <CustomButton
              handleOnClick={handleClearAllFilter}
              size={"small"}
              variant={"contained"}
              btnName={"Clear Filters "}
            />
          </Box>
        </Box>

        <Divider sx={{ mt: "10px" }} />

        {tabValue === 2 ? (
          <SortByOptionsTickets
            selectColumn={selectColumn}
            selectOrder={selectOrder}
            setSelectColumn={setSelectColumn}
            setSelectOrder={setSelectOrder}
          />
        ) : null}
        <Box
          sx={{
            minHeight: "400px",
            overflowY: "auto",
          }}
        >
          {filterSections.map((data, index) => (
            <div
              key={index}
              style={{
                marginTop: "5px",
                padding: "2px",
              }}
            >
              <FilterSection
                data={data}
                reqObj={reqObj}
                fetchTickets={fetchTickets}
                setSearchTicketId={setSearchTicketId}
                setSearchIntrId={setSearchIntrId}
                selectedTicketsType={selectedTicketsType}
                setSelectedTicketsType={setSelectedTicketsType}
                handleCheckboxChange={handleCheckboxChange}
                selectTicketStatus={selectTicketStatus}
                selectOEM={selectOEM}
                selectTECH={selectTECH}
                selectedZones={selectedZones}
                selectedEE={selectedEE}
                selectedVC={selectedVC}
                selectedStandard={selectedStandard}
                selectedChapter={selectedChapter}
                selectedLR={selectedLR}
                selectSWMEE={selectSWMEE}
                selectedSWM={selectedSWM}
                ticketsType={ticketsType}
              />
            </div>
          ))}
        </Box>
      </Box>
    </div>
  );
}

export default FilterByOptionsTickets;

const MemoizedTextField = React.memo(
  ({ setSearchTicketId, setSearchIntrId, data, fetchTickets, reqObj }) => {
    const [inputValue, setInputValue] = useState(data?.value || "");
    const memoizedSearchChange = useCallback((e, title) => {
      if (title === "Ticket ID") {
        setSearchTicketId(e.target.value);
      } else if (title === "Intersection ID") {
        setSearchIntrId(e.target.value);
      }
    }, []);

    const onClickFunction = (title, value) => {
      let updatedReqObj = { ...reqObj };
      if (title === "Ticket ID") {
        updatedReqObj.ticket_id = value;
      } else if (title === "Intersection ID") {
        updatedReqObj.intr_id = value;
      }
      return updatedReqObj;
    };

    const handleIconButtonClick = () => {
      const updatedReqObj = onClickFunction(data?.title, data?.value);
      fetchTickets(updatedReqObj);
    };

    const handleClearClick = () => {
      setInputValue("");
      memoizedSearchChange({ target: { value: "" } }, data?.title);
      fetchTickets(reqObj);
    };

    return (
      <TextField
        autoComplete="off"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          memoizedSearchChange(e, data?.title);
        }}
        sx={{ width: "100%" }}
        variant="standard"
        label={data?.title}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {inputValue?.length > 0 && (
                <IconButton edge="end" onClick={handleClearClick}>
                  <ClearIcon />
                </IconButton>
              )}
              <IconButton
                disabled={
                  (data?.title === "Ticket ID" && inputValue?.length < 6) ||
                    (data?.title === "Intersection ID" && inputValue?.length < 1)
                    ? true
                    : false
                }
                edge="end"
                onClick={handleIconButtonClick}
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  },
);
